import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import * as XLSX from 'xlsx';
import { ClockIcon, MagnifyingGlassIcon, FunnelIcon, ArrowDownTrayIcon, CalendarIcon } from "@heroicons/react/24/outline";
import { getTeamAttendanceRecords } from "../../../services/employee.service";

const TeamAttendanceReports = () => {
  const [attendanceData, setAttendanceData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [loading, setLoading] = useState(false);
  const [selectedEmployees, setSelectedEmployees] = useState([]);
  const [searchTerm, setSearchTerm] = useState("");
  const [dateRange, setDateRange] = useState({ from: "", to: "" });
  const [uniqueEmployees, setUniqueEmployees] = useState([]);

  useEffect(() => {
    fetchTeamAttendance();
  }, []);

  useEffect(() => {
    applyFilters();
  }, [attendanceData, selectedEmployees, dateRange]);

  const fetchTeamAttendance = async () => {
    setLoading(true);
    try {
      const data = await getTeamAttendanceRecords(200);
      setAttendanceData(data);
      
      const employees = [...new Set(data.map(r => r.emp_code))].map(code => {
        const record = data.find(r => r.emp_code === code);
        return { code, name: record.emp_name };
      });
      setUniqueEmployees(employees);
    } catch (err) {
      console.error("Failed to fetch team attendance", err);
    } finally {
      setLoading(false);
    }
  };

  const applyFilters = () => {
    let filtered = [...attendanceData];

    if (selectedEmployees.length > 0) {
      filtered = filtered.filter(r => selectedEmployees.includes(r.emp_code));
    }

    if (dateRange.from) {
      filtered = filtered.filter(r => new Date(r.date) >= new Date(dateRange.from));
    }

    if (dateRange.to) {
      filtered = filtered.filter(r => new Date(r.date) <= new Date(dateRange.to));
    }

    setFilteredData(filtered);
  };

  const toggleEmployeeSelection = (empCode) => {
    setSelectedEmployees(prev =>
      prev.includes(empCode) ? prev.filter(e => e !== empCode) : [...prev, empCode]
    );
  };

  const downloadExcel = () => {
    const dataToExport = filteredData.length > 0 ? filteredData : attendanceData;
    
    const metadataRows = [
      { 'Report Information': 'Report Type', 'Value': 'Team Attendance Records' },
      { 'Report Information': 'Generated By', 'Value': localStorage.getItem("emp_code") || "MANAGER" },
      { 'Report Information': 'Generation Time', 'Value': new Date().toLocaleString() },
      { 'Report Information': 'Total Records', 'Value': dataToExport.length },
      { 'Report Information': '', 'Value': '' }
    ];

    const headerRow = {
      'Employee Code': 'Employee Code',
      'Employee Name': 'Employee Name',
      'Date': 'Date',
      'Check-In': 'Check-In',
      'Check-Out': 'Check-Out',
      'Work Hours': 'Work Hours',
      'Status': 'Status'
    };

    const dataRows = dataToExport.map(record => ({
      'Employee Code': record.emp_code,
      'Employee Name': record.emp_name,
      'Date': record.date,
      'Check-In': record.check_in_time,
      'Check-Out': record.check_out_time,
      'Work Hours': record.work_hours,
      'Status': record.status
    }));

    const allRows = [...metadataRows, headerRow, ...dataRows];
    const ws = XLSX.utils.json_to_sheet(allRows);
    ws['!cols'] = [{ wch: 20 }, { wch: 25 }, { wch: 15 }, { wch: 12 }, { wch: 12 }, { wch: 12 }, { wch: 12 }];

    const wb = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(wb, ws, 'Team Attendance');
    XLSX.writeFile(wb, `team_attendance_${new Date().getTime()}.xlsx`);
  };

  const filteredEmployees = uniqueEmployees.filter(emp =>
    emp.code.toLowerCase().includes(searchTerm.toLowerCase()) ||
    emp.name.toLowerCase().includes(searchTerm.toLowerCase())
  );

  return (
    <div className="min-h-screen bg-[#f8fafc] p-6">
      <div className="max-w-[1600px] mx-auto">
        <div className="flex justify-between items-center mb-6">
          <div className="flex items-center gap-3">
            <div className="p-2 bg-blue-700 rounded shadow-lg">
              <ClockIcon className="h-7 w-7 text-white" />
            </div>
            <h1 className="text-2xl font-bold text-slate-900">Team Attendance Reports</h1>
          </div>
          <button
            onClick={downloadExcel}
            className="flex items-center gap-2 bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded-lg font-bold"
          >
            <ArrowDownTrayIcon className="h-5 w-5" />
            Download Excel
          </button>
        </div>

        <div className="grid grid-cols-1 lg:grid-cols-4 gap-6">
          <div className="lg:col-span-1 bg-white rounded-lg border p-4 h-fit">
            <h3 className="text-sm font-bold text-slate-700 mb-4 flex items-center gap-2">
              <FunnelIcon className="h-5 w-5" />
              Filters
            </h3>

            <div className="space-y-4">
              <div>
                <label className="text-xs font-bold text-slate-600 mb-2 block">Date Range</label>
                <input
                  type="date"
                  value={dateRange.from}
                  onChange={(e) => setDateRange(prev => ({ ...prev, from: e.target.value }))}
                  className="w-full border rounded p-2 text-sm mb-2"
                />
                <input
                  type="date"
                  value={dateRange.to}
                  onChange={(e) => setDateRange(prev => ({ ...prev, to: e.target.value }))}
                  className="w-full border rounded p-2 text-sm"
                />
              </div>

              <div>
                <label className="text-xs font-bold text-slate-600 mb-2 block">Select Employees</label>
                <div className="relative mb-2">
                  <MagnifyingGlassIcon className="h-4 w-4 absolute left-2 top-1/2 -translate-y-1/2 text-slate-400" />
                  <input
                    type="text"
                    placeholder="Search employees..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    className="w-full pl-8 pr-3 py-2 border rounded text-sm"
                  />
                </div>
                <div className="max-h-64 overflow-y-auto border rounded p-2 space-y-1">
                  {filteredEmployees.map(emp => (
                    <label key={emp.code} className="flex items-center gap-2 p-2 hover:bg-slate-50 rounded cursor-pointer">
                      <input
                        type="checkbox"
                        checked={selectedEmployees.includes(emp.code)}
                        onChange={() => toggleEmployeeSelection(emp.code)}
                        className="rounded"
                      />
                      <span className="text-sm">{emp.name} ({emp.code})</span>
                    </label>
                  ))}
                </div>
                {selectedEmployees.length > 0 && (
                  <button
                    onClick={() => setSelectedEmployees([])}
                    className="mt-2 text-xs text-red-600 hover:underline"
                  >
                    Clear Selection ({selectedEmployees.length})
                  </button>
                )}
              </div>
            </div>
          </div>

          <div className="lg:col-span-3 bg-white rounded-lg border overflow-hidden">
            <div className="p-4 border-b bg-slate-50">
              <h3 className="text-sm font-bold text-slate-700">
                Attendance Records ({filteredData.length || attendanceData.length})
              </h3>
            </div>

            {loading ? (
              <div className="p-10 text-center text-slate-400">Loading attendance data...</div>
            ) : (
              <div className="overflow-x-auto">
                <table className="min-w-full">
                  <thead className="bg-slate-50 text-slate-700 text-xs font-bold uppercase">
                    <tr>
                      <th className="py-3 px-4 text-left">Employee Code</th>
                      <th className="py-3 px-4 text-left">Employee Name</th>
                      <th className="py-3 px-4 text-left">Date</th>
                      <th className="py-3 px-4 text-left">Check-In</th>
                      <th className="py-3 px-4 text-left">Check-Out</th>
                      <th className="py-3 px-4 text-left">Work Hours</th>
                      <th className="py-3 px-4 text-center">Status</th>
                    </tr>
                  </thead>
                  <tbody className="divide-y divide-slate-100">
                    {(filteredData.length > 0 ? filteredData : attendanceData).map((record, idx) => (
                      <tr key={idx} className="hover:bg-blue-50/30">
                        <td className="py-3 px-4 text-sm font-bold text-slate-900">{record.emp_code}</td>
                        <td className="py-3 px-4 text-sm text-slate-700">{record.emp_name}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{record.date}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{record.check_in_time}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{record.check_out_time}</td>
                        <td className="py-3 px-4 text-sm text-slate-600">{record.work_hours}</td>
                        <td className="py-3 px-4 text-center">
                          <span className={`text-xs font-bold px-3 py-1 rounded-full ${
                            record.status === 'PRESENT' ? 'bg-green-100 text-green-700' :
                            record.status === 'HALF_DAY' ? 'bg-yellow-100 text-yellow-700' :
                            'bg-red-100 text-red-700'
                          }`}>
                            {record.status}
                          </span>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default TeamAttendanceReports;
